import { useEffect, useState } from 'react';
import {
  Box,
  ProgressBar,
  ProgressBarProps,
  Text,
} from '@nimbus-ds/components';

const DEFAULT_MIN_ANIMATION_DURATION = 5000;
const DEFAULT_MIN_ANIMATION_TARGET_PROGRESS_PERCENTAGE = 90;
const DEFAULT_COMPLETION_TIMEOUT = 400;
interface FakeProgressBarProps {
  promise: Promise<unknown>;
  appearance: ProgressBarProps['appearance'];
  minAnimationTargetProgressPercentage?: number;
  minAnimationDuration?: number;
  completionTimeout?: number;
  steps: { name: string; duration: number }[];
  onFinish: () => void;
  onError: () => void;
}

function FakeProgressBar({
  promise,
  appearance,
  minAnimationTargetProgressPercentage = DEFAULT_MIN_ANIMATION_TARGET_PROGRESS_PERCENTAGE,
  minAnimationDuration = DEFAULT_MIN_ANIMATION_DURATION,
  completionTimeout = DEFAULT_COMPLETION_TIMEOUT,
  steps = [],
  onError,
  onFinish,
}: Readonly<FakeProgressBarProps>) {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const minDuration = minAnimationDuration;

  useEffect(() => {
    let rafId: number | null = null;
    let completionTimeoutId: NodeJS.Timeout | null = null;
    let finished = false;
    let promiseResolved = false;

    const startTime = performance.now();

    // eslint-disable-next-line max-statements
    const animate = () => {
      const now = performance.now();
      const elapsed = now - startTime;

      // 1. CALCULATE PROGRESS BAR PROGRESS
      // The bar should reach MIN_ANIMATION_TARGET_PROGRESS_PERCENTAGE in MIN_ANIMATION_DURATION
      // Example: 40% in 10000ms
      const barProgressPercent =
        Math.min(elapsed / minDuration, 1) *
        minAnimationTargetProgressPercentage;
      const currentBarProgress = Math.round(
        promiseResolved ? 100 : barProgressPercent,
      );

      setProgress(currentBarProgress);

      // 2. CALCULATE STEPS
      // Steps are completed in MIN_ANIMATION_TARGET_PROGRESS_PERCENTAGE of total time
      // Example: steps are completed in 4000ms (40% of 10000ms)
      const localSteps = steps || [];
      if (localSteps.length > 0) {
        const stepsTimeWindow =
          (minDuration * minAnimationTargetProgressPercentage) / 100;
        const totalStepsDuration = localSteps.reduce(
          (sum, step) => sum + step.duration,
          0,
        );

        // Calculate what percentage of steps we have completed
        const stepsProgressPercent =
          Math.min(elapsed / stepsTimeWindow, 1) * 100;
        const stepsProgress = (stepsProgressPercent / 100) * totalStepsDuration;

        let accumulatedDuration = 0;
        let currentStepIndex = 0;

        for (let i = 0; i < localSteps.length; i++) {
          accumulatedDuration += localSteps[i].duration;
          if (stepsProgress <= accumulatedDuration) {
            currentStepIndex = i;
            break;
          }
          currentStepIndex = i;
        }

        setCurrentStep((prev) =>
          prev === currentStepIndex ? prev : currentStepIndex,
        );
      }

      // 3. CONTROL WHEN TO END THE ANIMATION
      // Continue until MIN_ANIMATION_DURATION has passed AND the promise is resolved
      const shouldContinue = !promiseResolved;
      if (shouldContinue) {
        rafId = requestAnimationFrame(animate);
      } else if (!finished) {
        // Minimum time completed and promise resolved
        finished = true;
        setProgress(100);
        if (completionTimeoutId) {
          clearTimeout(completionTimeoutId);
        }
        completionTimeoutId = setTimeout(() => {
          onFinish();
        }, completionTimeout);
      }
    };

    rafId = requestAnimationFrame(animate);

    promise.catch(onError);

    // When the promise finishes, mark as resolved
    promise.finally(() => {
      promiseResolved = true;
      // Don't complete immediately, let the animation finish naturally
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (completionTimeoutId) clearTimeout(completionTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="2"
      width="100%"
      justifyContent="center"
      alignItems="center"
    >
      {steps.length > 0 && <Text>{steps[currentStep].name}</Text>}
      <ProgressBar appearance={appearance} value={progress} />
    </Box>
  );
}

export default FakeProgressBar;
